import { useEffect, useState } from "react";
import {
    Card,
    CardContent,
    TextField,
    Typography,
    MenuItem,
    Box,
    Button,
    Tooltip,
} from "@material-ui/core";
import useDealer from "hooks/useDealer";
import useDealerId from "hooks/useDealerId";
import { useBackdrop } from "hooks/useBackdrop";
import { useSnackbar } from "hooks/useSnackbar";
import ExportService from "services/ExportService";
import { BASE_URL_API } from "services/Http";

const languages = [
    { id: "en", name: "English" },
    { id: "es", name: "Spanish" },
    { id: "pt", name: "Portuguese" },
    { id: "fr", name: "French" },
];

export default function IsuzuFilterOptions({ selectedCategories = [] }) {
    const dealerId = useDealerId();
    const { openBackdrop, closeBackdrop } = useBackdrop();
    const [language, setLanguage] = useState("es");
    const [platform, setPlatform] = useState("Google Platform");
    const [region, setRegion] = useState("USA");
    const snackbar = useSnackbar();

    const dealer = useDealer();

    useEffect(() => {
        const availableLangs = dealer?.languagesAvailable?.split(",") || [];

        if (!availableLangs.includes(language)) {
            setLanguage(availableLangs[0] || "");
        }

        if (dealer?.country) {
            setRegion(dealer?.country);
        }
    }, [dealer, language]);

    // Función reutilizable para generar el Excel
    const generateExcelFile = async () => {
        const products = await ExportService.fetchMarketplaceData({
            dealerId,
            loc: language,
            catalogs: ["isuzu_fv"],
            platform,
            categories: selectedCategories,
            region,
        });

        let excelResponse;

        if (platform === "Google Platform") {
            excelResponse = await ExportService.generateGoogleExcel({
                dealerId,
                products,
            });
        } else {
            excelResponse = await ExportService.generateMLExcel({
                dealerId,
                products,
            });
        }

        return { response: excelResponse, products };
    };

    const handleExportClick = async () => {
        try {
            openBackdrop("Exporting data, please wait...");

            const { response: excelResponse } = await generateExcelFile();

            if (excelResponse?.status === 200) {
                const excelFilePath = excelResponse?.data?.filePath || "";

                // --- FIX START ---
                // If the backend returns a full absolute URL (S3/Assets Domain),
                // use it directly. Otherwise, prepend the baseDownloadUrl.
                if (excelFilePath.startsWith("http")) {
                    window.location.href = excelFilePath;
                } else {
                    const baseDownloadUrl = BASE_URL_API.replace(/\/api$/, "");
                    window.location.href = `${baseDownloadUrl}${excelFilePath}`;
                }
                // --- FIX END ---

                setTimeout(async () => {
                    try {
                        // Extract fileName correctly for deletion
                        // (works for both relative paths and absolute URLs)
                        const fileName = excelFilePath.split("/").pop();
                        await ExportService.deleteMLExcel({
                            dealerId,
                            fileName: fileName,
                        });
                    } catch (error) {
                        console.error("Failed to delete Excel file:", error);
                    }
                }, 20000);
            }
        } catch (error) {
            console.error("❌ Export failed:", error);
        } finally {
            closeBackdrop();
        }
    };

    const handleUploadToFtp = async () => {
        try {
            openBackdrop("Uploading file to FTP, please wait...");

            const { response: excelResponse } = await generateExcelFile();

            if (excelResponse?.status === 200) {
                const fileName = excelResponse.data.filePath.split("/").pop();

                await ExportService.uploadExcelToFTP({
                    dealerId,
                    fileName,
                    remoteFolder: "/",
                });

                snackbar("success", "Upload Complete, File successfully uploaded to FTP.");
            } else {
                snackbar("error", "Upload Failed, Could not generate Excel file.");
            }
        } catch (error) {
            console.error("❌ Upload to FTP failed:", error);

            const backendMessage =
                error?.response?.data?.error || "An unexpected error occurred during FTP upload.";

            snackbar("error", `Upload Failed, ${backendMessage}`);
        } finally {
            closeBackdrop();
        }
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Select Language
                </Typography>

                <TextField
                    select
                    label="Select a language"
                    fullWidth
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    margin="normal"
                >
                    {languages
                        .filter((lang) => dealer?.languagesAvailable?.includes(lang.id))
                        .map((lang) => (
                            <MenuItem key={lang.id} value={lang.id}>
                                {lang.name}
                            </MenuItem>
                        ))}
                </TextField>

                <Box textAlign="center" mt={3} display="flex" justifyContent="center">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleExportClick}
                        disabled={selectedCategories.length === 0}
                    >
                        Export
                    </Button>

                    <Tooltip title="Upload generated file to Google FTP" arrow>
                        <span>
                            <Button
                                variant="outlined"
                                color="secondary"
                                disabled={selectedCategories.length === 0}
                                onClick={handleUploadToFtp}
                                style={{ marginLeft: 12 }}
                            >
                                Upload to FTP
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </CardContent>
        </Card>
    );
}
